// Turning a class template into the occurrences people actually book.
//
// A template stores WALL-CLOCK time plus an IANA zone, not an instant. "Monday
// 6:00pm" must stay 6:00pm across a DST boundary — storing a UTC instant and
// adding 7 days would silently shift the class by an hour twice a year.

const DAY_MS = 86400000;

export const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** YYYY-MM-DD for a Date, in UTC. Template dates are calendar dates. */
export function toDateKey(date) {
  return date.toISOString().slice(0, 10);
}

/**
 * YYYY-MM-DD for a Date as seen in a specific zone.
 *
 * "What day is it" is a question about where the gym is, not where the server
 * is. A Chicago studio at 8pm is already on tomorrow's date in UTC, so a
 * UTC-derived today() shows tomorrow's schedule, counts usage against the wrong
 * week, and stamps joined_on a day late. en-CA formats as YYYY-MM-DD.
 */
export function dateKeyInZone(date, timeZone = 'UTC') {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(date);
}

export function fromDateKey(key) {
  return new Date(`${key}T00:00:00.000Z`);
}

export function addDays(key, n) {
  return toDateKey(new Date(fromDateKey(key).getTime() + n * DAY_MS));
}

export function weekdayOf(key) {
  return WEEKDAYS[fromDateKey(key).getUTCDay()];
}

/**
 * Minutes east of UTC for a zone at a given instant. Resolved through Intl so
 * we inherit the platform's tz database rather than shipping one.
 */
export function offsetAt(instantMs, timeZone) {
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const p = Object.fromEntries(fmt.formatToParts(new Date(instantMs)).map((x) => [x.type, x.value]));
  const asIfUTC = Date.UTC(
    Number(p.year), Number(p.month) - 1, Number(p.day),
    Number(p.hour) % 24, Number(p.minute), Number(p.second),
  );
  return (asIfUTC - instantMs) / 60000;
}

/**
 * The real UTC instant for a wall-clock slot in a zone.
 *
 * Treat the wall clock as if it were UTC, then correct by the zone's offset at
 * that moment. One correction pass is exact everywhere except inside a DST
 * transition hour, where a wall time is either ambiguous or nonexistent; there
 * we land on a defensible neighbouring instant rather than throwing, because a
 * 2am class is not a real scenario worth failing a booking over.
 */
export function instantFor(dateKey, timeHHMM, timeZone) {
  const [h, m] = timeHHMM.split(':').map(Number);
  const [Y, M, D] = dateKey.split('-').map(Number);
  const guess = Date.UTC(Y, M - 1, D, h, m);
  const offset = offsetAt(guess, timeZone);
  return new Date(guess - offset * 60000);
}

/**
 * Expand a template across [fromKey, toKey] inclusive.
 *
 * template: {
 *   id, name, weekdays: ['mon','wed'], time: '18:00', durationMin: 60,
 *   capacity: 20, startDate: 'YYYY-MM-DD', endDate?: 'YYYY-MM-DD',
 *   timeZone: 'America/Chicago', staffId, roomId,
 *   exceptions?: ['YYYY-MM-DD'],             // cancelled outright
 *   overrides?: { 'YYYY-MM-DD': { ...patch } } // substitute, time change, cap change
 * }
 */
export function expandTemplate(template, fromKey, toKey) {
  const {
    weekdays = [], startDate, endDate, exceptions = [], overrides = {},
    timeZone = 'UTC', time, durationMin,
  } = template;

  const begin = startDate && startDate > fromKey ? startDate : fromKey;
  const finish = endDate && endDate < toKey ? endDate : toKey;
  if (begin > finish) return [];

  const out = [];
  for (let key = begin; key <= finish; key = addDays(key, 1)) {
    if (!weekdays.includes(weekdayOf(key))) continue;
    if (exceptions.includes(key)) continue;

    const patch = overrides[key] || {};
    const slotTime = patch.time || time;
    const duration = patch.durationMin ?? durationMin;
    const startsAt = instantFor(key, slotTime, timeZone);

    out.push({
      id: `${template.id}:${key}`,
      templateId: template.id,
      date: key,
      name: patch.name || template.name,
      staffId: patch.staffId || template.staffId,
      roomId: patch.roomId || template.roomId,
      capacity: patch.capacity ?? template.capacity,
      time: slotTime,
      timeZone,
      durationMin: duration,
      startsAt: startsAt.toISOString(),
      endsAt: new Date(startsAt.getTime() + duration * 60000).toISOString(),
      substituted: Boolean(patch.staffId && patch.staffId !== template.staffId),
    });
  }
  return out;
}

/** Expand many templates and return them in chronological order. */
export function buildSchedule(templates, fromKey, toKey) {
  return templates
    .flatMap((t) => expandTemplate(t, fromKey, toKey))
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));
}
